import type { TaskPerson } from "@esi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type RefObject, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError } from "../api-errors.ts";
import { useDebounced } from "../conversation/ConversationList.tsx";
import { AssigneeLookupUnavailableError, lookupAssignees, taskKeys } from "./queries.ts";

/** Saved assignment is independent of lookup results: missing people never become UUID labels. */
export function AssigneePicker({
  projectId,
  taskId,
  value,
  onChange,
  disabled = false,
  closeList,
}: {
  projectId: string;
  taskId?: string;
  value: TaskPerson | null;
  onChange: (p: TaskPerson | null) => void;
  disabled?: boolean;
  closeList?: RefObject<(() => boolean) | null>;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const id = useId();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const settled = useDebounced(text.trim());
  const result = useQuery({
    queryKey: taskKeys.people(projectId, taskId, settled),
    queryFn: ({ signal }) => lookupAssignees(projectId, settled, taskId, signal),
    enabled: !disabled && settled.length >= 2 && settled.length <= 100,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  const unavailable =
    result.isError &&
    result.error instanceof ApiError &&
    (result.error.status === 404 || result.error instanceof AssigneeLookupUnavailableError);
  useEffect(() => {
    if (
      result.isError &&
      taskId &&
      result.error instanceof ApiError &&
      (result.error.status === 403 || result.error.status === 404)
    ) {
      void client.invalidateQueries({ queryKey: taskKeys.detail(projectId, taskId) });
    }
  }, [result.isError, result.error, client, projectId, taskId]);
  const fresh = text.trim() === settled && !result.isFetching && !result.isError;
  const people = fresh ? (result.data ?? []) : [];
  const expanded = open && people.length > 0 && !disabled;
  useEffect(() => {
    if (!closeList) {
      return;
    }
    closeList.current = () => {
      if (!expanded) {
        return false;
      }
      setOpen(false);
      return true;
    };
    return () => {
      closeList.current = null;
    };
  }, [closeList, expanded]);
  const choose = (p: TaskPerson | undefined) => {
    if (fresh && p) {
      onChange(p);
      setOpen(false);
      setText("");
    }
  };
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {/* The current value, and clearing it, on one row aligned with the fields. */}
      <div className="flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1">
        <p className="text-sm text-fg">
          {value ? (value.name ?? t("tasks.unavailablePerson")) : t("tasks.unassigned")}
        </p>
        {value ? (
          <Button
            size="xs"
            variant="outline"
            type="button"
            disabled={disabled || unavailable}
            onClick={() => onChange(null)}
          >
            {t("tasks.unassigned")}
          </Button>
        ) : null}
      </div>
      <label htmlFor={id} className="text-sm font-medium">
        {t("tasks.searchPeople")}
      </label>
      <Input
        id={id}
        role="combobox"
        autoComplete="off"
        value={text}
        disabled={disabled || unavailable}
        aria-expanded={expanded}
        aria-controls={expanded ? `${id}-list` : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          expanded ? `${id}-${Math.min(active, people.length - 1)}` : undefined
        }
        aria-describedby={`${id}-hint`}
        onChange={(e) => {
          setText(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            setActive((a) =>
              people.length
                ? (a + (e.key === "ArrowDown" ? 1 : -1) + people.length) % people.length
                : 0,
            );
          }
          if (e.key === "Enter") {
            e.preventDefault();
            if (expanded) {
              choose(people[Math.min(active, people.length - 1)]);
            }
          }
        }}
      />
      {expanded ? (
        <div
          id={`${id}-list`}
          role="listbox"
          aria-label={t("tasks.lookupResults")}
          className="max-h-48 overflow-y-auto rounded-lg border border-line bg-surface-raised"
        >
          {people.map((person, i) => (
            <div
              key={person.userId}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`cursor-pointer px-3 py-2 text-sm ${i === active ? "bg-surface-hover" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => setActive(i)}
              onClick={() => choose(person)}
              onKeyDown={() => {}}
              tabIndex={-1}
            >
              {person.name ?? t("tasks.unavailablePerson")}
            </div>
          ))}
        </div>
      ) : null}
      <p id={`${id}-hint`} role="status" className="text-xs text-fg-muted">
        {unavailable
          ? t("tasks.lookupUnavailable")
          : result.isError
            ? result.error.message
            : text.trim().length < 2
              ? t("tasks.peopleHint")
              : result.isFetching || !fresh
                ? t("app.loading")
                : people.length === 0
                  ? t("tasks.peopleNone")
                  : null}
      </p>
      {result.isError ? (
        <Button
          size="sm"
          variant="outline"
          type="button"
          className="self-start"
          disabled={result.isFetching}
          onClick={() => void result.refetch()}
        >
          {t("app.retry")}
        </Button>
      ) : null}
    </div>
  );
}
